import { useState } from 'react';
import { beans } from '../data/beans';
import Badge from '../components/Badge';
import ScoreBar from '../components/ScoreBar';

const filters = [
  { id: 'all',    label: 'All' },
  { id: 'single', label: 'Single Estate' },
  { id: 'blend',  label: 'Blends' },
  { id: 'light',  label: 'Light Roast' },
  { id: 'medium', label: 'Medium Roast' },
  { id: 'dark',   label: 'Dark Roast' },
];

export default function Shop() {
  const [active,  setActive]  = useState('all');
  const [sort,    setSort]    = useState('featured');
  const [toast,   setToast]   = useState(null);
  const [leaving, setLeaving] = useState(false);
  const [cart,    setCart]    = useState([]);

  function handleAdd(bean) {
    setCart(p => [...p, bean.id]);
    setLeaving(false);
    setToast(bean);
    clearTimeout(window._toastTimer);
    window._toastTimer = setTimeout(() => {
      setLeaving(true);
      setTimeout(() => setToast(null), 300);
    }, 2800);
  }

  const filtered = beans
    .filter(b => active === 'all' || b.filter.includes(active))
    .sort((a, b) => {
      if (sort === 'score')  return b.score - a.score;
      if (sort === 'price')  return a.price - b.price;
      if (sort === 'newest') return b.id - a.id;
      return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
    });

  return (
    <div className="bg-cream page-enter relative">

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-8 left-1/2 z-50 ${leaving ? 'toast-out' : 'toast-in'}`}
          style={{ transform: 'translateX(-50%)' }}>
          <div className="bg-maroon text-light px-6 py-4 flex items-center gap-4 shadow-2xl min-w-[300px]">
            <img src={toast.img} alt="" className="w-10 h-10 object-cover flex-shrink-0"
              onError={e=>e.target.style.display='none'} />
            <div className="flex-1">
              <div className="text-[10px] font-medium text-sand/55 uppercase tracking-widest mb-0.5">Added to cart</div>
              <div className="text-sm text-light/90 truncate">{toast.name}</div>
            </div>
            <div className="text-xs text-sand/35 pl-3 border-l border-sand/15 flex-shrink-0">{cart.length} item{cart.length!==1?'s':''}</div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-maroon px-8 md:px-16 py-20">
        <p className="text-[10px] tracking-[0.25em] uppercase text-sand/40 mb-5">Home Brewing Collection</p>
        <h1 className="font-serif font-light text-light leading-tight mb-5" style={{ fontSize: 'clamp(2.5rem,5vw,4rem)' }}>
          India's Finest<br /><em className="text-sand/70">Coffee Beans</em>
        </h1>
        <p className="text-sm text-sand/45 leading-[1.85] max-w-md">
          All 80+ scored, freshly roasted in-house, shipped directly to your door —
          the same beans we brew in our cafés.
        </p>
      </div>

      {/* Filters */}
      <div className="flex items-center border-b border-maroon/10 px-8 md:px-16 overflow-x-auto bg-offwhite">
        {filters.map(f => (
          <button key={f.id} onClick={() => setActive(f.id)}
            className={`text-[10px] tracking-[0.15em] uppercase py-5 px-4 border-b-2 whitespace-nowrap transition-all duration-300 font-medium ${
              active === f.id
                ? 'border-maroon text-maroon'
                : 'border-transparent text-maroon/35 hover:text-maroon/65'
            }`}>
            {f.label}
          </button>
        ))}
        <div className="ml-auto flex-shrink-0 pl-6">
          <select value={sort} onChange={e => setSort(e.target.value)}
            className="text-[10px] text-maroon/40 bg-transparent border-none outline-none cursor-pointer tracking-wide py-5">
            <option value="featured">Featured</option>
            <option value="score">Score: High to Low</option>
            <option value="price">Price: Low to High</option>
            <option value="newest">Newest</option>
          </select>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-[20px] bg-sand/20 px-8 md:px-16 mb-20 py-px mb-0">
        {filtered.map(bean => (
          <div key={bean.id}
            className={`hover-lift group flex flex-col ${bean.featured ? 'bg-maroon' : 'bg-offwhite'}`}>

            {/* Product image */}
            <a href={bean.productUrl} target="_blank" rel="noreferrer"
              className="block overflow-hidden flex-shrink-0" style={{ height: '400px' }}>
              <img src={bean.img} alt={bean.name}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                onError={e => e.target.style.display='none'} />
            </a>

            <div className="flex flex-col flex-1 p-8">
              <div className="flex items-start justify-between gap-2 mb-4">
                <Badge type={bean.badge} label={bean.badgeLabel} />
                <span className={`text-[10px] tracking-widest uppercase text-right ${bean.featured ? 'text-sand/40' : 'text-maroon/35'}`}>
                  {bean.origin}
                </span>
              </div>

              <a href={bean.productUrl} target="_blank" rel="noreferrer"
                className={`font-serif text-xl font-light leading-snug mb-1.5 hover:opacity-70 transition-opacity ${bean.featured ? 'text-light' : 'text-maroon'}`}>
                {bean.name}
              </a>
              <div className={`text-xs mb-5 ${bean.featured ? 'text-sand/40' : 'text-maroon/40'}`}>{bean.process}</div>

              <div className="flex gap-1.5 flex-wrap mb-5">
                {bean.notes.map(n => (
                  <span key={n} className={`text-[10px] px-2.5 py-1 rounded-full ${bean.featured ? 'bg-sand/10 text-sand/55' : 'bg-maroon/6 text-maroon/50'}`}>{n}</span>
                ))}
              </div>

              <ScoreBar score={bean.score} dark={bean.featured} />

              <div className="flex items-end justify-between mt-6 pt-5 border-t border-current/10">
                <div>
                  <div className={`font-serif text-xl font-light ${bean.featured ? 'text-light' : 'text-maroon'}`}>
                    {bean.priceRange.split('–')[0].trim()}
                  </div>
                  <div className={`text-[10px] mt-0.5 ${bean.featured ? 'text-sand/30' : 'text-maroon/30'}`}>
                    250g · up to {bean.priceRange.split('–')[1]?.trim()}
                  </div>
                </div>
                <button onClick={() => handleAdd(bean)}
                  className={`text-[10px] font-medium tracking-[0.12em] uppercase px-5 py-2.5 transition-all duration-300 active:scale-95 ${
                    bean.featured
                      ? 'bg-sand text-maroon hover:bg-light'
                      : 'bg-maroon text-light hover:bg-wine'
                  }`}>
                  Add to Cart
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Trust strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 bg-maroon px-8 md:px-16">
        {[
          { icon: '🚚', title: 'Free Shipping', sub: 'Across all of India' },
          { icon: '🌿', title: 'Ethically Sourced', sub: "Direct from India's estates" },
          { icon: '🔥', title: 'Roasted to Order', sub: 'Same beans as our cafés' },
          { icon: '↩',  title: 'Easy Returns',    sub: 'We make it right' },
        ].map((t, i) => (
          <div key={i} className="px-6 py-8 border-r border-sand/8 last:border-r-0 flex gap-4 items-start">
            <span className="text-lg flex-shrink-0 mt-0.5 opacity-60">{t.icon}</span>
            <div>
              <div className="text-xs font-medium text-light/80 mb-1 tracking-wide">{t.title}</div>
              <div className="text-[11px] text-sand/35 leading-relaxed">{t.sub}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
